import { EventLocation } from '../types/Event';

/**
 * Utility functions for handling event locations
 */

// Helper to check if location is an object or legacy string
export const isLocationObject = (location: EventLocation | string): location is EventLocation => {
  return typeof location === 'object' && location !== null && 'name' in location;
};

// Parse location string that might contain both name and address (format: "name|address")
const parseLocationString = (locationString: string): EventLocation => {
  if (locationString.includes('|')) {
    const [name, address] = locationString.split('|', 2);
    return {
      name: name || '',
      address: address || ''
    };
  }

  return {
    name: locationString || '',
    address: ''
  };
};

// Get display name from location (handles both object and string formats)
export const getLocationDisplayName = (location: EventLocation | string): string => {
  if (isLocationObject(location)) {
    return location.name || 'Unknown Location';
  }

  // Handle the new combined format or legacy string
  const parsed = parseLocationString(location);
  return parsed.name || 'Unknown Location';
};

// Get address from location (returns empty string for legacy string locations)
export const getLocationAddress = (location: EventLocation | string): string => {
  if (isLocationObject(location)) {
    return location.address || '';
  }

  // Handle the new combined format
  const parsed = parseLocationString(location);
  return parsed.address || '';
};

// Generate universal mapping URL that works across devices
export const generateMapsUrl = (location: EventLocation | string): string => {
  let query = '';

  if (isLocationObject(location)) {
    // Use address if available, otherwise fall back to name
    query = location.address || location.name;
  } else {
    // Parse the combined format to get address first, then name
    const parsed = parseLocationString(location);
    query = parsed.address || parsed.name;
  }

  if (!query) return '';

  // Universal mapping URL that works on all platforms:
  // - iOS: Opens Apple Maps
  // - Android: Opens Google Maps
  // - Desktop: Opens default browser mapping service
  const encodedQuery = encodeURIComponent(query);
  return `https://maps.google.com/maps?q=${encodedQuery}`;
};

// Convert legacy string location to EventLocation object
export const normalizeLocation = (location: EventLocation | string): EventLocation => {
  if (isLocationObject(location)) {
    return location;
  }

  // Parse the combined format or legacy string
  return parseLocationString(location);
};

// Format location for display with optional address
export const formatLocationDisplay = (location: EventLocation | string, showAddress: boolean = true): string => {
  if (isLocationObject(location)) {
    const name = location.name || 'Unknown Location';
    if (showAddress && location.address) {
      return `${name} - ${location.address}`;
    }
    return name;
  }

  // Handle the new combined format
  const parsed = parseLocationString(location);
  const name = parsed.name || 'Unknown Location';
  if (showAddress && parsed.address) {
    return `${name} - ${parsed.address}`;
  }
  return name;
};
